import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';

import { logger } from '@treneo/shared/logger';

import { getApiBaseUrl } from '../config';

export type { Result } from '@treneo/models';

export class ApiClient {
  private readonly axiosInstance: AxiosInstance;
  private interceptorsSetup = false;

  constructor() {
    const baseURL = getApiBaseUrl();
    logger.info(
      `[ApiClient] created with baseURL: ${baseURL === '' ? '(empty, same-origin)' : baseURL}`
    );
    this.axiosInstance = axios.create({
      baseURL,
      timeout: 30000,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  async get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.get<T>(url, config);
  }

  async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.post<T>(url, data, config);
  }

  async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.put<T>(url, data, config);
  }

  async patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.patch<T>(url, data, config);
  }

  async delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.axiosInstance.delete<T>(url, config);
  }

  getInstance(): AxiosInstance {
    return this.axiosInstance;
  }

  async setupStoreInterceptors(
    _logoutHandler: () => void,
    setupInterceptors: (instance: AxiosInstance) => Promise<AxiosInstance>
  ): Promise<void> {
    if (this.interceptorsSetup) {
      return;
    }

    try {
      this.axiosInstance.interceptors.request.clear();
      this.axiosInstance.interceptors.response.clear();

      await setupInterceptors(this.axiosInstance);
      this.interceptorsSetup = true;
    } catch (error) {
      logger.warn('Failed to setup store interceptors:', error);
    }
  }

  isSetup(): boolean {
    return this.interceptorsSetup;
  }

  resetInterceptors(): void {
    this.axiosInstance.interceptors.request.clear();
    this.axiosInstance.interceptors.response.clear();
    this.interceptorsSetup = false;
  }
}

let apiClientInstance: ApiClient | null = null;

export const getApiClient = (): ApiClient => {
  apiClientInstance ??= new ApiClient();
  return apiClientInstance;
};
